import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactSelect from "react-select";

const today = () => new Date().toISOString().slice(0, 10);

const challanTypes = [
  { value: "returnable", label: "Returnable DC" },
  { value: "non_returnable", label: "Billing DC" },
  { value: "inward", label: "Inward DC" }
];

const emptyItem = () => ({
  productId: "",
  itemCode: "",
  hsnCode: "",
  description: "",
  quantity: "",
  unit: "No"
});

const emptyForm = () => ({
  challanType: "returnable",
  dcNo: "",
  dcDate: today(),
  referenceNo: "",
  referenceDate: today(),
  customerId: "",
  customerName: "",
  customerPhone: "",
  customerContactPerson: "",
  customerEmail: "",
  customerGst: "",
  customerAddress: "",
  note: "",
  status: "draft",
  items: [emptyItem()]
});

// eslint-disable-next-line no-unused-vars
const inputClassName =
  "w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm text-stone-700 outline-none transition focus:border-amber-400";

const unitOptions = [
  ["Pkt", "Pkt"],
  ["No", "No"],
  ["Set", "Set"],
  ["Meter", "Meter"],
  ["Kg", "Kg"],
  ["Litre", "Litre"],
  ["Length", "Length"],
  ["Job", "Job"],
  ["Days", "Days"],
  ["Month", "Month"]
];

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatAddress = (customer = {}) => {
  const billingAddress = customer.billingAddress || {};
  const workAddress = customer.address || {};
  const hasBillingAddress = Object.values(billingAddress).some(Boolean);
  const address = hasBillingAddress ? billingAddress : workAddress;
  return [
    address.street,
    address.area,
    address.city,
    address.state,
    address.pincode
  ].filter(Boolean).join(", ");
};

const typeLabel = (value) =>
  challanTypes.find((item) => item.value === value)?.label || "Delivery Challan";

const normalizeProductValue = (value = "") => String(value || "").trim().toLowerCase();

const getStatusBadge = (status = "draft") => {
  const normalized = String(status || "draft").toLowerCase();
  let label = "Draft";
  let classes = "bg-stone-100 text-stone-700 border-stone-300";
  
  if (normalized === "approved") {
    label = "Approved";
    classes = "bg-blue-100 text-blue-750 border-blue-300";
  } else if (normalized === "dispatched") {
    label = "Dispatched";
    classes = "bg-amber-100 text-amber-700 border-amber-300";
  } else if (normalized === "received") {
    label = "Received";
    classes = "bg-amber-100 text-amber-700 border-amber-300";
  } else if (normalized === "closed") {
    label = "Closed";
    classes = "bg-emerald-100 text-emerald-700 border-emerald-300";
  } else if (normalized === "cancelled") {
    label = "Cancelled";
    classes = "bg-rose-100 text-rose-700 border-rose-300";
  }
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${classes}`}>
      {label}
    </span>
  );
};

export default function DeliveryChallans() {
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "";
  const [customers, setCustomers] = useState([]);
  const [challans, setChallans] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeType = searchParams.get("type") || "returnable";

  useEffect(() => {
    setEditingId("");
    setForm({
      ...emptyForm(),
      challanType: activeType,
      dcNo: activeType === "inward" ? "" : "",
      dcDate: activeType === "inward" ? "" : today()
    });
  }, [activeType]);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const customerOptions = useMemo(() => {
    return customers.map((c) => ({
      value: c._id,
      label: c.name || c.customerId || "",
      data: c
    }));
  }, [customers]);

  const selectedCustomerOption = useMemo(() => {
    if (!form.customerId) {
      const matched = customers.find(
        (c) => (c.name || c.customerId || "") === form.customerName
      );
      if (matched) return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
      return form.customerName ? { value: "", label: form.customerName } : null;
    }
    const matched = customers.find((c) => c._id === form.customerId);
    if (matched) {
      return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
    }
    return form.customerName ? { value: form.customerId, label: form.customerName } : null;
  }, [customers, form.customerId, form.customerName]);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "36px",
      borderRadius: "0.25rem",
      borderColor: state.isFocused ? "#2563eb" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
      fontSize: "0.875rem",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#2563eb"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 60,
      borderRadius: "0.375rem",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#dbeafe"
        : state.isFocused
          ? "#eff6ff"
          : "#ffffff",
      color: "#0f172a",
      fontSize: "0.875rem"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };

  const loadData = async () => {
    try {
      const [customersRes, challansRes, productsRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/customers").catch(err => { console.error("Failed to load customers", err); return { data: [] }; }),
        axios.get(API_BASE_URL + "/api/delivery-challans").catch(err => { console.error("Failed to load challans", err); return { data: [] }; }),
        axios.get(API_BASE_URL + "/api/products", { params: { limit: 200 } }).catch(err => { console.error("Failed to load products", err); return { data: { products: [] } }; })
      ]);

      const getProductsArray = (resData) => {
        if (!resData) return [];
        if (Array.isArray(resData)) return resData;
        if (Array.isArray(resData.products)) return resData.products;
        return [];
      };

      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setChallans(Array.isArray(challansRes.data) ? challansRes.data : []);
      setProducts(getProductsArray(productsRes.data));
    } catch (error) {
      console.error("Failed to load delivery challans data", error);
      setCustomers([]);
      setChallans([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredChallans = useMemo(() => {
    const query = search.trim().toLowerCase();
    return challans.filter((challan) => {
      const matchesType = challan.challanType === activeType;
      const haystack = [
        challan.dcNo,
        challan.referenceNo,
        challan.customerName,
        challan.customerPhone,
        challan.customerGst,
        ...(challan.items || []).map((item) => item.description)


      ].join(" ").toLowerCase();
      return matchesType && (!query || haystack.includes(query));
    });
  }, [activeType, challans, search]);

  // eslint-disable-next-line no-unused-vars
  const selectedCustomerDetails = [
    form.customerContactPerson ? `Contact: ${form.customerContactPerson}` : "",
    form.customerPhone ? `No: ${form.customerPhone}` : "",
    form.customerEmail,
    form.customerGst ? `GST: ${form.customerGst}` : ""
  ].filter(Boolean).join(" | ");

  const setType = (type) => {
    setSearchParams({ type });
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    if (name === "customerName") {
      const matched = customers.find((customer) =>
        [customer.name, customer.customerId]
          .filter(Boolean)
          .some((entry) => entry.toLowerCase() === value.trim().toLowerCase())
      );

      setForm((current) => ({
        ...current,
        customerName: value,
        customerId: matched?._id || "",
        customerPhone: matched?.phone || "",
        customerContactPerson: matched?.contactPerson || "",
        customerEmail: matched?.email || "",
        customerGst: matched?.gstNumber || matched?.additionalDetails?.gstNumber || "",
        customerAddress: matched ? formatAddress(matched) : current.customerAddress
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const updateItem = (index, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const applyProduct = (index, value) => {
    const query = normalizeProductValue(value);
    if (!query) return;

    const matched = products.find((product) =>
      [product.itemCode, product.description, product.hsnCode]
        .filter(Boolean)
        .some((entry) => normalizeProductValue(entry) === query)
    );

    if (!matched) return;

    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex !== index
          ? item
          : {
            ...item,
            productId: String(matched._id || ""),
            itemCode: matched.itemCode || "",
            hsnCode: matched.hsnCode || "",
            description: matched.description || item.description,
            unit: formatUnit(matched.unit || item.unit)
          }
      ))
    }));
  };

  const updateItemWithProduct = (index, field, value) => {
    updateItem(index, field, value);
    if (["description", "itemCode", "hsnCode"].includes(field)) {
      applyProduct(index, value);
    }
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [...current.items, emptyItem()]
    }));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1


        ? [emptyItem()]
        : current.items.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const resetForm = () => {
    setEditingId("");
    setForm({
      ...emptyForm(),
      challanType: activeType,
      dcDate: activeType === "inward" ? "" : today()
    });
  };

  const submitChallan = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      challanType: activeType,
      items: form.items
        .filter((item) => item.description.trim() || item.quantity)
        .map((item) => ({
          productId: item.productId || "",
          itemCode: item.itemCode || "",
          hsnCode: item.hsnCode || "",
          description: item.description.trim(),
          quantity: Number(item.quantity) || 0,
          unit: formatUnit(item.unit)
        }))
    };

    try {
      setSaving(true);
      if (editingId) {
        await axios.put(API_BASE_URL + `/api/delivery-challans/${editingId}`, payload);
      } else {
        await axios.post(API_BASE_URL + "/api/delivery-challans", payload);
      }
      resetForm();
      await loadData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save delivery challan");
    } finally {
      setSaving(false);
    }
  };

  const editChallan = (challan) => {
    setEditingId(challan._id);
    setForm({
      ...emptyForm(),
      ...challan,
      dcDate: challan.dcDate ? new Date(challan.dcDate).toISOString().slice(0, 10) : "",
      referenceDate: challan.referenceDate ? new Date(challan.referenceDate).toISOString().slice(0, 10) : "",
      items: challan.items?.length
        ? challan.items.map((item) => ({
            productId: item.productId || "",
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            description: item.description || "",
            quantity: String(item.quantity ?? ""),
            unit: formatUnit(item.unit)
          }))
        : [emptyItem()]
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openChallanPdf = async (challan) => {
    try {
      const response = await axios.get(API_BASE_URL + `/api/delivery-challans/${challan._id}/pdf`, {
        responseType: "blob"
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const pdfWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");

      if (!pdfWindow) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 30000);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to open delivery challan PDF");
    }
  };

  const downloadChallanPdf = async (challan) => {
    try {
      const response = await axios.get(API_BASE_URL + `/api/delivery-challans/${challan._id}/pdf?download=1`, {
        responseType: "blob"
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${challan.dcNo || challan.referenceNo || "challan"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 30000);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to download delivery challan PDF");
    }
  };

  const openSalesInvoiceFromChallan = (challan) => {
    const role = localStorage.getItem("role") || "";
    navigate(role === "admin" ? "/admin/sales-invoices" : "/accounts/sales-invoices", {
      state: {
        accountDocumentPrefill: {
          source: "delivery-challan",
          entryType: "sale",
          customerId: challan.customerId || "",
          counterpartyName: challan.customerName || "",
          counterpartyPhone: challan.customerPhone || "",
          counterpartyEmail: challan.customerEmail || "",
          counterpartyGst: challan.customerGst || "",
          counterpartyAddress: challan.customerAddress || "",
          deliveryAddressType: "work_address",
          transactionDate: today(),
          reference: challan.dcNo || challan.referenceNo || "",
          sourceDeliveryChallanId: challan._id || "",
          sourceDeliveryChallanNo: challan.dcNo || challan.referenceNo || "",
          sourceDeliveryChallanType: challan.challanType || "",
          skipInventoryDeduction: challan.challanType === "non_returnable",
          notes: challan.note || "",
          entries: (challan.items || []).map((item) => ({
            productId: item.productId || "",
            itemName: item.description || item.itemCode || "",
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            description: item.description || "",
            quantity: String(item.quantity ?? ""),
            unit: formatUnit(item.unit),
            listPrice: "",
            discountType: "percent",
            discountValue: "",
            unitPrice: "",
            totalAmount: ""
          }))
        },
        prefillKey: `sales-invoice-from-dc-${challan._id}-${Date.now()}`
      }
    });
  };

  return (
    <div className="mobile-dense-page space-y-3 overflow-x-hidden p-3 bg-slate-50 min-h-screen text-slate-800">
      {/* SECTION 1: PAGE HEADER */}
      <section className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <nav className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
              ACCOUNTS / {activeType === "returnable" ? "RETURNABLE DC" : activeType === "non_returnable" ? "BILLING DC" : "INWARD DC"}
            </nav>
            <h1 className="text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-1.5">
              <span className="text-blue-600">📄</span> {editingId ? "Edit" : "Create"} {typeLabel(activeType)}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              form="delivery-challan-form"
              disabled={saving}
              className="rounded-[4px] bg-blue-600 hover:bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 h-9 flex items-center justify-center gap-1.5 min-w-[130px]"
            >
              <span>💾</span> {saving ? "Saving..." : editingId ? `Update ${typeLabel(activeType)}` : `Save ${typeLabel(activeType)}`}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-4 py-1.5 text-sm font-semibold text-slate-700 transition h-9 flex items-center justify-center gap-1.5"
              >
                <span>✕</span> Cancel
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* SECTION 1B: TAB SELECTION */}
      <section className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {challanTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setType(type.value)}
              className={`rounded-[4px] border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition ${
                activeType === type.value
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </section>

      <form id="delivery-challan-form" onSubmit={submitChallan} className="space-y-4">
        
        {/* Top Cards Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          
          {/* Card A: Customer Profile */}
          <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">👥</span> Customer & Contact
            </h2>
            
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-500">Customer <span className="text-red-500">*</span></label>
                <ReactSelect
                  options={customerOptions}
                  value={selectedCustomerOption}
                  styles={reactSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  onChange={(option) => {
                    if (option) {
                      setForm((current) => ({
                        ...current,
                        customerName: option.label,
                        customerId: option.value,
                        customerPhone: option.data?.phone || "",
                        customerContactPerson: option.data?.contactPerson || "",
                        customerEmail: option.data?.email || "",
                        customerGst: option.data?.gstNumber || option.data?.additionalDetails?.gstNumber || "",
                        customerAddress: option.data ? formatAddress(option.data) : current.customerAddress
                      }));
                    } else {
                      setForm((current) => ({
                        ...current,
                        customerName: "",
                        customerId: "",
                        customerPhone: "",
                        customerContactPerson: "",
                        customerEmail: "",
                        customerGst: "",
                        customerAddress: ""
                      }));
                    }
                  }}
                  placeholder="Search customer name or ID..."
                  isClearable
                />
              </div>
              <div>
                <InputField
                  label="Contact Person"
                  name="customerContactPerson"
                  value={form.customerContactPerson}
                  onChange={handleFieldChange}
                />
              </div>
              <div>
                <InputField
                  label="Phone"
                  name="customerPhone"
                  value={form.customerPhone}
                  onChange={handleFieldChange}
                />
              </div>
              <div>
                <InputField
                  label="Email"
                  name="customerEmail"
                  value={form.customerEmail}
                  onChange={handleFieldChange}
                />
              </div>
              <div>
                <InputField
                  label="GST No"
                  name="customerGst"
                  value={form.customerGst}
                  onChange={handleFieldChange}
                />
              </div>
              <div className="sm:col-span-2">
                <InputField
                  label="Reference No"
                  name="referenceNo"
                  value={form.referenceNo}
                  onChange={handleFieldChange}
                />
              </div>
            </div>
          </div>

          {/* Card B: Challan Details */}
          <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">📄</span> {typeLabel(activeType)} Details
            </h2>
            
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              {activeType !== "inward" ? (
                <>
                  <div>
                    <InputField
                      label="DC No"
                      name="dcNo"
                      value={form.dcNo}
                      onChange={handleFieldChange}
                      placeholder="Auto-generated"
                    />
                  </div>
                  <div>
                    <InputField
                      label="DC Date"
                      name="dcDate"
                      value={form.dcDate}
                      onChange={handleFieldChange}
                      type="date"
                    />
                  </div>
                </>
              ) : null}
              <div>
                <InputField
                  label="Reference Date"
                  name="referenceDate"
                  value={form.referenceDate}
                  onChange={handleFieldChange}
                  type="date"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">Status</label>
                <select
                  name="status"
                  value={form.status || "draft"}
                  onChange={handleFieldChange}
                  className="w-full rounded-[4px] border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9 bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  {activeType === "inward" ? (
                    <option value="received">Received</option>
                  ) : (
                    <option value="dispatched">Dispatched</option>
                  )}
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

        </div>

        {/* Section 3: Challan Items */}
        <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">🛒</span> {typeLabel(activeType)} Items
            </h2>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={addItem}
                className="rounded-[4px] border border-blue-300 bg-blue-50/50 hover:bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
              >
                <span>+</span> Add Item
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("Clear all item rows?")) {
                    setForm((current) => ({ ...current, items: [emptyItem()] }));
                  }
                }}
                className="rounded-[4px] border border-red-200 bg-red-50/30 hover:bg-red-50 text-red-600 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
              >
                <span>🗑️</span> Clear All
              </button>
            </div>
          </div>

          <datalist id="delivery-challan-products">
            {products.flatMap((product) => ([
              <option key={`${product._id}-code`} value={product.itemCode || ""} label={product.description || ""} />,
              <option key={`${product._id}-desc`} value={product.description || ""} label={product.itemCode || ""} />,
              <option key={`${product._id}-hsn`} value={product.hsnCode || ""} label={product.description || ""} />
            ]))}
          </datalist>

          {/* Grid-based spreadsheet table */}
          <div className="max-w-full overflow-x-auto pb-1">
            <div className="min-w-[680px] overflow-visible rounded-[4px] border border-slate-200 bg-white">
              {/* Header row */}
              <div className="grid bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-600" style={{gridTemplateColumns: "40px 1fr 100px 100px 80px"}}>
                <span className="p-2 text-center border-r border-slate-200">#</span>
                <span className="p-2 border-r border-slate-200">Material Description</span>
                <span className="p-2 text-right border-r border-slate-200">Qty</span>
                <span className="p-2 border-r border-slate-200">Unit</span>
                <span className="p-2 text-center">Action</span>
              </div>

              {/* Rows */}
              {form.items.map((item, index) => (
                <div key={`dc-item-row-${index}`} className="grid border-b border-slate-200 hover:bg-slate-50/40 items-center" style={{gridTemplateColumns: "40px 1fr 100px 100px 80px"}}>
                  <div className="p-1 border-r border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500">
                    {index + 1}
                  </div>
                  <div className="p-1 border-r border-slate-200">
                    <input
                      value={item.description}
                      onChange={(event) => updateItemWithProduct(index, "description", event.target.value)}
                      className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8"
                      placeholder="Material description"
                      list="delivery-challan-products"
                    />
                  </div>
                  <div className="p-1 border-r border-slate-200">
                    <input
                      value={item.quantity}
                      onChange={(event) => updateItem(index, "quantity", event.target.value)}
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-sm outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 text-right font-medium"
                      placeholder="0"
                    />
                  </div>
                  <div className="p-1 border-r border-slate-200">
                    <select
                      value={formatUnit(item.unit)}
                      onChange={(event) => updateItem(index, "unit", event.target.value)}
                      className="w-full rounded-[4px] border border-slate-200 bg-white px-1 h-8 text-xs outline-none focus:border-blue-500 font-medium"
                    >
                      {unitOptions.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="p-1 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-400 hover:text-red-600 transition"
                      title="Remove row"
                    >
                      <span className="text-xs">🗑️</span>
                    </button>
                  </div>
                </div>
              ))}

              {form.items.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No items yet. Click + Add Item to begin.</div>
              )}

              {/* Add New Row button centered inside container */}
              <div className="p-2 border-t border-slate-200 bg-white flex justify-center">
                <button
                  type="button"
                  onClick={addItem}
                  className="w-full max-w-xs border border-dashed border-blue-300 rounded-[4px] py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/50 transition flex items-center justify-center gap-1"
                >
                  <span>+</span> Add New Row
                </button>
              </div>

              {/* Bottom Spreadsheet Info Bar */}
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-600">
                <div>Total Items: {form.items.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Area: Grid for Address & Notes (left) + Summary (right - not applicable, so just 2 cards) */}
        <div className="grid gap-4 md:grid-cols-5 items-stretch">
          
          {/* Column 1: Logistics & Delivery Vectors */}
          <div className="md:col-span-2 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">📍</span> Billing & Shipping Address
            </h2>
            <div>
              <textarea
                name="customerAddress"
                value={form.customerAddress}
                onChange={handleFieldChange}
                rows={4}
                placeholder="Enter billing & shipping address details here..."
                className="w-full rounded-[4px] border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y min-h-[100px] text-slate-700"
              />
            </div>
          </div>

          {/* Column 2: Contractual Clauses & Context */}
          <div className="md:col-span-3 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">📝</span> Notes / Terms
            </h2>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Notes / Remarks</label>
              <textarea
                name="note"
                value={form.note}
                onChange={handleFieldChange}
                rows={4}
                placeholder="Material movement note"
                className="w-full rounded-[4px] border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y h-[100px] text-slate-700"
              />
            </div>
          </div>

        </div>

      </form>

      {/* SECTION 6: CHALLAN HISTORY */}
      <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <span className="text-blue-600 text-sm">📜</span> {typeLabel(activeType)} History
            </h2>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search challans..."
              className="rounded-[4px] border border-slate-300 px-3 py-1 text-sm outline-none focus:border-blue-500 h-9 bg-white w-full sm:w-64 text-slate-700"
            />
          </div>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[920px] border-collapse text-left text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <th className="px-3 py-2 font-semibold border-r border-slate-100">Date</th>
                <th className="px-3 py-2 font-semibold border-r border-slate-100">DC / Reference</th>
                <th className="px-3 py-2 font-semibold border-r border-slate-100">Customer</th>
                <th className="px-3 py-2 font-semibold border-r border-slate-100">Products</th>
                <th className="px-3 py-2 font-semibold border-r border-slate-100">Status</th>
                <th className="px-3 py-2 font-semibold border-r border-slate-100">Note</th>
                <th className="px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-400">Loading challans...</td></tr>
              ) : filteredChallans.length === 0 ? (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-400">No challans found.</td></tr>
              ) : filteredChallans.map((challan) => (
                <tr key={challan._id} className="border-b border-slate-200 hover:bg-slate-50/50 align-middle">
                  <td className="px-3 py-2 text-slate-600 border-r border-slate-100">
                    {formatDate(challan.challanType === "inward" ? challan.referenceDate : challan.dcDate)}
                  </td>
                  <td className="px-3 py-2 font-semibold text-slate-900 border-r border-slate-100">
                    <p className="font-semibold text-slate-900">{challan.dcNo || challan.referenceNo || "-"}</p>
                    <p className="text-[10px] text-slate-500 font-normal">Ref: {challan.referenceNo || "-"}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-700 border-r border-slate-100 font-medium">
                    {challan.customerId ? (
                      <button
                        type="button"
                        onClick={() => navigate(role === "accounts" ? `/accounts/customer/${challan.customerId}` : `/customer/${challan.customerId}`)}
                        className="text-blue-600 hover:text-blue-850 hover:underline font-semibold text-left"
                      >
                        {challan.customerName || "-"}
                      </button>
                    ) : (
                      challan.customerName || "-"
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 border-r border-slate-100">
                    {(challan.items || []).slice(0, 2).map((item) => `${item.description} (${item.quantity || 0} ${formatUnit(item.unit)})`).join(", ") || "-"}
                    {(challan.items || []).length > 2 ? ` +${challan.items.length - 2} more` : ""}
                  </td>
                  <td className="px-3 py-2 border-r border-slate-100">
                    {getStatusBadge(challan.status)}
                  </td>
                  <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{challan.note || "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        onClick={() => openChallanPdf(challan)}
                        className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 transition"
                      >
                        PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadChallanPdf(challan)}
                        className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 transition"
                      >
                        Download PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => editChallan(challan)}
                        className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 transition"
                      >
                        Edit
                      </button>
                      {challan.challanType === "non_returnable" ? (
                        <button
                          type="button"
                          onClick={() => openSalesInvoiceFromChallan(challan)}
                          className="rounded-[4px] border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700 transition"
                        >
                          Sales Invoice
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div>
      {label && <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>}
      <input {...props} className="w-full rounded-[4px] border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9 bg-white" />
    </div>
  );
}
